import axios from "axios";
import { Script } from "gatsby";
import { useAtomValue } from "jotai";
import { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Helmet } from "react-helmet";

import {
  FontFacesLoader,
  RenderBlockingScript,
  ToastProvider,
  ketchAtom,
  useDyActiveConsent,
  useIsKeyboardUser,
  useKetch,
} from "@ms/gatsby-theme";

import { LS_ACCESS_TOKEN } from "@/config/constants";

const ThirdPartyScripts = () => {
  const ketchCookies = useAtomValue(ketchAtom);

  // Only add third party scripts on netlify deploys
  return process.env.NETLIFY ? (
    <>
      <Script id="ketch">
        {`!function(){var e=new URLSearchParams(document.location.search),a=e.has("property")?e.get("property"):"manscaped_com",o=document.createElement("script");o.type="text/javascript",o.src="https://global.ketchcdn.com/web/v2/config/manscaped/".concat(a,"/boot.js"),o.defer=o.async=!0,document.getElementsByTagName("head")[0].appendChild(o),window.semaphore=window.semaphore||[]}();`}
      </Script>
      <Script id="quantum-metric">
        {`(function() {var qtm = document.createElement('script'); qtm.type = 'text/javascript'; qtm.async = 1;qtm.src = 'https://cdn.quantummetric.com/qscripts/quantum-manscaped.js';var d = document.getElementsByTagName('script')[0];!window.QuantumMetricAPI && d.parentNode.insertBefore(qtm, d);})();`}
      </Script>
      {process.env.DY_SECTION_ID && (
        <>
          <Helmet>
            <link rel="preconnect" href="https://cdn.dynamicyield.com" />
            <link rel="preconnect" href="https://st.dynamicyield.com" />
            <link rel="preconnect" href="https://rcom.dynamicyield.com" />
            <link rel="dns-prefetch" href="https://cdn.dynamicyield.com" />
            <link rel="dns-prefetch" href="https://st.dynamicyield.com" />
            <link rel="dns-prefetch" href="https://rcom.dynamicyield.com" />
          </Helmet>
          <RenderBlockingScript
            url={`https://cdn.dynamicyield.com/api/${process.env.DY_SECTION_ID}/api_dynamic.js`}
          />
          <RenderBlockingScript
            url={`https://cdn.dynamicyield.com/api/${process.env.DY_SECTION_ID}/api_static.js`}
          />
        </>
      )}
      {ketchCookies.performance_cookies === true && (
        <Script src="https://cdn.attn.tv/manscaped/dtag.js" />
      )}
    </>
  ) : null;
};

const Fallback = () => {
  return (
    <>
      <p>Unknown Error. Please refresh and retry!</p>
      <p>
        If this problem persists, please reach out to au@manscaped.com for support. We apologize for
        any inconvenience.
      </p>
    </>
  );
};

export const RootLayout = ({ children }: { children: ReactNode }) => {
  // Add .keyboard-user to body if user uses keyboard
  useIsKeyboardUser();

  useKetch();

  useDyActiveConsent();

  return (
    <>
      <Helmet>
        <title>Manscaped</title>
        <html lang="en" />
      </Helmet>
      <ErrorBoundary FallbackComponent={Fallback}>
        <ToastProvider>
          <FontFacesLoader />
          {children}
          <ThirdPartyScripts />
        </ToastProvider>
      </ErrorBoundary>
    </>
  );
};

export default RootLayout;

axios.interceptors.request.use((request) => {
  request.baseURL = `${process.env.MS_API_URL}/api`;

  if (typeof window !== "undefined") {
    const accessToken = localStorage.getItem(LS_ACCESS_TOKEN);
    if (accessToken && request.headers) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  return request;
});
